import {
    getMessageBodyHtml,
    getMessageBodyText,
    getMessageDate,
    getMessageId,
    getMessageSubject,
    stripMailboxHtml,
    stripMailboxSubjectPrefixes,
} from '../../../helpers';

const messageKeys = (message) => {
    const keys = [
        message?.id,
        message?.message_id,
        message?.messageId,
    ];

    return keys
        .filter((key) => key != null && String(key).trim() !== '')
        .map((key) => String(key));
};

const parentKey = (message) => {
    const parentId = message?.in_reply_to_parent_id ?? message?.inReplyToParentId;

    if (parentId != null && String(parentId).trim() !== '') {
        return String(parentId);
    }

    const replyTo = message?.in_reply_to ?? message?.inReplyTo;

    if (replyTo != null && String(replyTo).trim() !== '') {
        return String(replyTo).trim();
    }

    return null;
};

const messageTime = (message) => {
    const value = getMessageDate(message) || message?.sent_at || message?.sentAt || '';
    const time = Date.parse(value);

    return Number.isFinite(time) ? time : 0;
};

const wouldCycle = (parent, child) => {
    let current = parent;

    while (current) {
        if (current === child) {
            return true;
        }

        current = current.parent;
    }

    return false;
};

export const buildCaseThreadTree = (items = []) => {
    const nodes = items.map((message, index) => {
        const id = getMessageId(message);

        return {
            message,
            children: [],
            parent: null,
            key: id != null ? String(id) : `message-${index}`,
        };
    });
    const byKey = new Map();

    nodes.forEach((node) => {
        messageKeys(node.message).forEach((key) => {
            if (!byKey.has(key)) {
                byKey.set(key, node);
            }
        });
    });

    const roots = [];

    nodes.forEach((node) => {
        const parentId = parentKey(node.message);
        const parent = parentId ? byKey.get(parentId) : null;

        if (parent && parent !== node && !wouldCycle(parent, node)) {
            node.parent = parent;
            parent.children.push(node);
            return;
        }

        roots.push(node);
    });

    const sortNodes = (list) => {
        list.sort((left, right) => messageTime(left.message) - messageTime(right.message));
        list.forEach((node) => sortNodes(node.children));
    };

    sortNodes(roots);

    return roots;
};

const flattenNodes = (nodes = []) => {
    const list = [];

    const walk = (items) => {
        items.forEach((node) => {
            list.push(node);
            walk(node.children);
        });
    };

    walk(nodes);

    return list;
};

export const listThreadMessages = (nodes = []) => {
    return flattenNodes(nodes).sort((left, right) => (
        messageTime(right.message) - messageTime(left.message)
    ));
};

export const getConversationSubject = (nodes = []) => {
    const list = flattenNodes(nodes);

    if (!list.length) {
        return '';
    }

    const oldest = list.reduce((current, node) => (
        messageTime(node.message) < messageTime(current.message) ? node : current
    ));

    return stripMailboxSubjectPrefixes(getMessageSubject(oldest.message));
};

export const collectThreadAncestors = (node) => {
    const ancestors = [];
    let current = node?.parent;

    while (current) {
        ancestors.push(current);
        current = current.parent;
    }

    return ancestors;
};

export const getMessagePreview = (message) => {
    const html = getMessageBodyHtml(message);
    const text = html
        ? stripMailboxHtml(html)
        : getMessageBodyText(message);

    return String(text || '').replace(/\s+/g, ' ').trim();
};
